import React, { useState, useEffect } from "react";
import { Outlet, Link } from "react-router-dom";
import { Download } from "lucide-react";

import { Button } from "../components/ui/button";

// Constants for app download links
const APPLE_APP_STORE_URL = "https://apps.apple.com/ca/app/pagent/id6670614673";

export default function Layout() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);

      // Determine which section is currently active based on scroll position
      const sections = ["features", "competitions", "about"];
      const scrollPosition = window.scrollY + 100; // Adding offset for navbar height

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const top = element.offsetTop;
          const height = element.offsetHeight;

          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle navigation click
  const handleNavClick = (e, id) => {
    e.preventDefault();
    setActiveSection(id);

    // Smooth scroll to section
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    } else {
      // If element not found, likely on a different page, navigate to home with hash
      window.location.href = `/#${id}`;
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header
        className={`fixed top-4 z-50 w-full transition-all duration-200 px-4`}
      >
        <div
          className={`mx-auto max-w-screen-xl rounded-full transition-all duration-200 ${
            isScrolled ? "bg-gray-800/70 shadow-sm" : "bg-gray-800/70 shadow-sm"
          }`}
        >
          <div className="container flex h-16 items-center justify-between px-6">
            <Link to="/" className="flex items-center space-x-2 z-20">
              <img
                src="/images/pagent-logo-icon.png"
                alt="Pagent"
                className="h-8 w-8"
              />
              <img
                src="/images/pagent-logo-text.png"
                alt="Pagent"
                className="h-6"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-2 text-sm font-medium">
              <a
                href="#features"
                className={`px-4 py-2 rounded-full transition-all duration-200 text-[#ffffff] ${
                  activeSection === "features"
                    ? "bg-yellow-500 font-medium shadow-sm"
                    : "bg-gray-700/50 hover:bg-gray-700/80"
                }`}
                onClick={(e) => handleNavClick(e, "features")}
              >
                Features
              </a>
              <a
                href="#competitions"
                className={`px-4 py-2 rounded-full transition-all duration-200 text-[#ffffff] ${
                  activeSection === "competitions"
                    ? "bg-yellow-500 font-medium shadow-sm"
                    : "bg-gray-700/50 hover:bg-gray-700/80"
                }`}
                onClick={(e) => handleNavClick(e, "competitions")}
              >
                Competitions
              </a>
              <a
                href="#about"
                className={`px-4 py-2 rounded-full transition-all duration-200 text-[#ffffff] ${
                  activeSection === "about"
                    ? "bg-yellow-500 font-medium shadow-sm"
                    : "bg-gray-700/50 hover:bg-gray-700/80"
                }`}
                onClick={(e) => handleNavClick(e, "about")}
              >
                About
              </a>
            </nav>

            <div className="flex items-center">
              <Button
                onClick={() => window.open(APPLE_APP_STORE_URL, "_blank")}
                variant="default"
                size="sm"
                className="flex rounded-full bg-yellow-500 text-[#ffffff] hover:bg-yellow-600 border-0"
              >
                <Download className="mr-2 h-4 w-4 text-[#ffffff]" /> Get the App
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-0 mt-0">
        <Outlet />
      </main>

      <footer className="border-t py-8">
        <div className="container flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-sm text-muted-foreground">
            © 2024 Pagent. All rights reserved.
          </p>
          <div className="flex items-center space-x-4">
            <Link
              to="/terms"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Privacy
            </Link>
            <Link
              to="#"
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
